import React from 'react';
import { Box, Grid, Typography, Paper, Button } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Link, useNavigate } from 'react-router-dom';

import { PageHeader, MetricCard, StatusChip, SkeletonCards, ErrorState, PermissionState } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';
import useCustomerDashboardPage from './useCustomerDashboardPage';
import { useCart } from '../../contexts/CartContext';
import AdvertisementsContainer from './AdvertisementsContainer';

const CustomerDashboard = () => {
  const p = useCustomerDashboardPage();
  const navigate = useNavigate();
  const { cart, addToCart } = useCart();

  if (p.effectiveStatus === 'loading') {
    return (
      <Box>
        <SkeletonCards rows={1} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map((i) => <Grid item xs={6} sm={3} key={i}><SkeletonCards rows={1} /></Grid>)}
        </Grid>
        <SkeletonCards rows={3} />
      </Box>
    );
  }

  if (p.effectiveStatus === 'error') {
    return (
      <ErrorState
        title={p.t("Couldn't load your dashboard", { defaultValue: "Couldn't load your dashboard" })}
        description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
        request={p.errorInfo?.request}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={() => p.fetchAll(false)}
      />
    );
  }

  if (p.effectiveStatus === 'permission') {
    return (
      <PermissionState
        title={p.t("You don't have access to the Dashboard", { defaultValue: "You don't have access to the Dashboard" })}
        description={p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' })}
      />
    );
  }

  const b = p.billing;
  const cartTotal = cart?.totalPrice ?? 0;
  const recentOrders = p.orders.slice(0, 3);

  return (
    <Box>
      <PageHeader
        title={p.t('Welcome back, {{name}}', { name: localStorage.getItem('username') || p.t('there', { defaultValue: 'there' }), defaultValue: `Welcome back, ${localStorage.getItem('username') || 'there'}` })}
        subtitle={p.t("Here's what's happening with your orders", { defaultValue: "Here's what's happening with your orders" })}
        actions={[
          { label: p.t('Cart · {{count}} items · {{total}}', { count: cart?.itemCount || 0, total: formatCurrency(cartTotal), defaultValue: `Cart · ${cart?.itemCount || 0} items · ${formatCurrency(cartTotal)}` }), icon: ShoppingCartIcon, variant: 'outlined', onClick: () => navigate('/cart') },
          { label: p.refreshing ? p.t('Refreshing...', { defaultValue: 'Refreshing...' }) : p.t('Refresh'), icon: RefreshIcon, variant: 'outlined', onClick: p.handleRefresh, disabled: p.refreshing },
        ]}
      />

      {p.reminderInvoices.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderRadius: `${radius.lg}px`, flexWrap: 'wrap' }}
        >
          <Box
            sx={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'primary.main', color: 'primary.contrastText',
            }}
          >
            <NotificationsNoneIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1">
              {p.t('Friendly payment reminder', { defaultValue: 'Friendly payment reminder' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {p.reminderInvoices.length === 1
                ? (p.reminderInvoices[0].dueDate
                  ? p.t('Just a heads-up: invoice {{number}} is due on {{date}}.', {
                      number: p.reminderInvoices[0].invoiceNumber,
                      date: new Date(p.reminderInvoices[0].dueDate).toLocaleDateString(),
                      defaultValue: `Just a heads-up: invoice ${p.reminderInvoices[0].invoiceNumber} is due on ${new Date(p.reminderInvoices[0].dueDate).toLocaleDateString()}.`,
                    })
                  : p.t('Just a heads-up: invoice {{number}} is awaiting payment.', {
                      number: p.reminderInvoices[0].invoiceNumber,
                      defaultValue: `Just a heads-up: invoice ${p.reminderInvoices[0].invoiceNumber} is awaiting payment.`,
                    }))
                : p.t('Just a heads-up: {{count}} invoices are awaiting payment.', {
                    count: p.reminderInvoices.length,
                    defaultValue: `Just a heads-up: ${p.reminderInvoices.length} invoices are awaiting payment.`,
                  })}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/invoices')}>
            {p.t('View invoices', { defaultValue: 'View invoices' })}
          </Button>
        </Paper>
      )}

      <AdvertisementsContainer />

      <Grid container spacing={1.75} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Orders this month', { defaultValue: 'Orders this month' })} value={b.ordersThisMonth} onClick={() => navigate('/my-orders')} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Spend this month', { defaultValue: 'Spend this month' })} value={formatCurrency(b.spendThisMonth)} onClick={() => navigate('/invoices')} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Items in cart', { defaultValue: 'Items in cart' })} value={cart?.itemCount || 0} onClick={() => navigate('/cart')} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard
            label={p.t('Outstanding', { defaultValue: 'Outstanding' })}
            value={formatCurrency(b.outstandingTotal)}
            delta={b.unpaidCount > 0 ? { direction: 'neutral', text: p.t('{{count}} unpaid', { count: b.unpaidCount, defaultValue: `${b.unpaidCount} unpaid` }) } : undefined}
            onClick={() => navigate('/invoices')}
          />
        </Grid>
      </Grid>

      <Paper
        variant="outlined"
        sx={{
          p: 2.5, mb: 2, display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3.5 }, flexWrap: 'wrap',
          bgcolor: 'warning.light', borderColor: 'warning.main',
        }}
      >
        <Box sx={{ width: 44, height: 44, borderRadius: `${radius.md}px`, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <RequestQuoteIcon sx={{ color: 'warning.dark' }} />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3.5 }, flexWrap: 'wrap', minWidth: 0 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">{p.t('Outstanding', { defaultValue: 'Outstanding' })}</Typography>
            <Typography variant="subtitle1" fontWeight={700} color="warning.dark">{formatCurrency(b.outstandingTotal)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{p.t('Unpaid invoices', { defaultValue: 'Unpaid invoices' })}</Typography>
            <Typography variant="body2" fontWeight={700}>{b.unpaidCount}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{p.t('Paid this year', { defaultValue: 'Paid this year' })}</Typography>
            <Typography variant="body2" fontWeight={700}>{formatCurrency(b.paidThisYear)} <Typography component="span" variant="caption" color="text.secondary">· {p.t('{{count}} invoices', { count: b.paidCount, defaultValue: `${b.paidCount} invoices` })}</Typography></Typography>
          </Box>
        </Box>
        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/invoices')}>
          {p.t('View invoices', { defaultValue: 'View invoices' })}
        </Button>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1">{p.t('Buy again', { defaultValue: 'Buy again' })}</Typography>
        <Typography component={Link} to="/shop" variant="body2" fontWeight={600} sx={{ color: 'primary.main', textDecoration: 'none' }}>{p.t('Browse all products', { defaultValue: 'Browse all products' })}</Typography>
      </Box>
      {p.reorderItems.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">{p.t('Products you order will show up here for quick reordering.', { defaultValue: 'Products you order will show up here for quick reordering.' })}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.75} sx={{ mb: 2 }}>
          {p.reorderItems.map((item) => (
            <Grid item xs={6} md={3} key={item.productId}>
              <Paper variant="outlined" sx={{ p: 1.75 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{item.productName}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {item.lastOrderedAt ? p.t('Ordered {{date}}', { date: new Date(item.lastOrderedAt).toLocaleDateString(), defaultValue: `Ordered ${new Date(item.lastOrderedAt).toLocaleDateString()}` }) : ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight={700}>{formatCurrency(item.unitPrice || 0)}</Typography>
                  <Button size="small" variant="outlined" startIcon={<AddShoppingCartIcon fontSize="small" />} onClick={() => addToCart(item.productId, 1)}>
                    {p.t('Add')}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('Your coupons', { defaultValue: 'Your coupons' })}</Typography>
            {p.coupons.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('No active coupons right now.', { defaultValue: 'No active coupons right now.' })}</Typography>
            ) : (
              p.coupons.map((coupon) => (
                <Box key={coupon._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 'none' } }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: `${radius.md}px`, bgcolor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <LocalOfferIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {coupon.code} — {coupon.discountType === 'PERCENT' ? `${coupon.discountValue}%` : formatCurrency(coupon.discountValue)} {p.t('off')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{p.t('Applies automatically at checkout when eligible.', { defaultValue: 'Applies automatically at checkout when eligible.' })}</Typography>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1">{p.t('Recent orders', { defaultValue: 'Recent orders' })}</Typography>
              <Typography component={Link} to="/my-orders" variant="body2" fontWeight={600} sx={{ color: 'primary.main', textDecoration: 'none' }}>{p.t('All orders', { defaultValue: 'All orders' })}</Typography>
            </Box>
            {recentOrders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t("You haven't placed any orders yet.", { defaultValue: "You haven't placed any orders yet." })}</Typography>
            ) : (
              recentOrders.map((order, index) => (
                <Box key={order._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{p.t('Order #')}{order.orderNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''} · {p.t('{{count}} items', { count: order.orderItems?.length || (order.productId ? 1 : 0), defaultValue: `${order.orderItems?.length || (order.productId ? 1 : 0)} items` })}
                    </Typography>
                  </Box>
                  <StatusChip status={order.status} label={p.t(order.status)} />
                  <Typography variant="body2" fontWeight={700} sx={{ width: 80, textAlign: 'right' }}>{formatCurrency(order.totalPrice)}</Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerDashboard;
